import React, { useEffect, useRef, useState } from "react";

// Viewport-driven door map (Redfin model). Map bounds are the single source of
// truth: pan/zoom fetches whatever is in view (counted clusters when dense,
// individual doors when sparse). Tapping a pin opens a rich intelligence card
// (permit + contractor entrenchment + area momentum) fetched from /api/leads/:id.
// Owner-contact unlock is gated by a one-time compliance ack and files the lead
// into the user's Verified Focus bucket on success (server-side).
// Data: /api/leads/map  /api/geocode  /api/leads/:id  /api/focus  /api/leads/:id/unlock
//       /api/auth/me  /api/user/acknowledge-skiptrace

const API_URL = "https://solarlead-backend-production.up.railway.app";
const LIST_MAX = 150;
const DEBOUNCE_MS = 280;

type SegMeta = { label: string; color: string; heroTitle: string; heroSub: string };
const SEG: Record<string, SegMeta> = {
  all: { label: "All permits", color: "#FF8800",
    heroTitle: "Every solar permit, mapped to the door",
    heroSub: "Search any market for solar activity across the full installed base, mapped down to the door." },
  installers: { label: "New installs", color: "#FF8800",
    heroTitle: "Catch every new install first",
    heroSub: "Search any market for homes that just went solar, mapped down to the door." },
  cleaning: { label: "Maintenance & cleaning", color: "#0B8A87",
    heroTitle: "Find systems due for a service call",
    heroSub: "Search any city for solar installs old enough to need a cleaning or tune-up, mapped down to the door." },
  battery: { label: "Battery add-on", color: "#2970FF",
    heroTitle: "Find systems ready for a battery",
    heroSub: "Search any market for installs at the right age for a storage upsell, mapped down to the door." },
  takeover: { label: "System takeover", color: "#F43F7E",
    heroTitle: "Find orphaned systems to take over",
    heroSub: "Search any city for aging installs whose owners may be ready for a new service partner." },
  loanpayoff: { label: "Loan payoff", color: "#7C5CFC",
    heroTitle: "Find owners nearing loan payoff",
    heroSub: "Search any market for systems approaching the end of their finance term, mapped down to the door." },
  longterm: { label: "Replacement-ready", color: "#12B76A",
    heroTitle: "Find replacement-ready systems",
    heroSub: "Search any city for long-tenure systems with years of ownership behind them." },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const COMPLIANCE = [
  "Owner contact data is provided for legitimate business use only. You are solely responsible for how you use it.",
  "Before contacting anyone you must comply with all applicable laws, including the TCPA, CAN-SPAM, Canada's CASL, and all federal, state, and provincial Do-Not-Call and privacy rules. Scrub against DNC lists and honor every opt-out.",
  "SolarLead provides this data as is, makes no warranty as to its accuracy, and is not responsible or liable for how you use it.",
  "By continuing you confirm you have a lawful basis to contact this owner and you accept sole responsibility for compliance.",
];

let leafletPromise: Promise<any> | null = null;
function loadLeaflet(): Promise<any> {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    const w = window as any;
    if (!document.getElementById("leaflet-css")) {
      const l = document.createElement("link");
      l.id = "leaflet-css"; l.rel = "stylesheet";
      l.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(l);
    }
    if (w.L) { resolve(w.L); return; }
    const s = document.createElement("script");
    s.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    s.onload = () => resolve(w.L);
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return leafletPromise;
}

function ageParts(pd: any): { short: string; long: string } {
  const t = Date.parse(String(pd || ""));
  if (!isFinite(t)) return { short: "", long: "" };
  const days = Math.max(0, (Date.now() - t) / 86400000);
  let short = "";
  if (days < 60) short = Math.round(days) + "d";
  else if (days < 730) short = Math.round(days / 30.44) + "mo";
  else short = (days / 365.25).toFixed(1) + "yr";
  const d = new Date(t);
  return { short, long: MONTHS[d.getMonth()] + " " + d.getFullYear() };
}

function fmtCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k";
  return String(n);
}

function cleanPermitType(s: any): string {
  let t = String(s || "").trim();
  if (!t) return "";
  t = t.replace(/^CPPU\s*-\s*/i, "");
  t = t.replace(/\s*\/\s*[A-Z]\s*\/\s*Electrical General/i, "");
  t = t.replace(/\s{2,}/g, " ").trim();
  return t.length > 54 ? t.slice(0, 52) + "\u2026" : t;
}

function trendColor(tr: string): string {
  return tr === "accelerating" ? "#16A34A" : tr === "cooling" ? "#DC2626" : "#262B37";
}

// Pull system size + battery presence out of the free-text permit description.
// Backslash-free patterns on purpose (keeps the patch tooling honest).
function parseKw(desc: string): string | null {
  if (!desc) return null;
  const m = desc.match(/([0-9]+(?:[.][0-9]+)?) *kw/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (!isFinite(v) || v <= 0 || v > 1000) return null;
  return (v >= 100 ? String(Math.round(v)) : String(Math.round(v * 10) / 10)) + " kW";
}
function parseBattery(desc: string): string | null {
  if (!desc) return null;
  const d = desc.toLowerCase();
  if (d.indexOf("powerwall") !== -1) return "Powerwall";
  if (d.indexOf("encharge") !== -1 || d.indexOf("enphase battery") !== -1 || d.indexOf("enphase iq battery") !== -1) return "Enphase battery";
  if (d.indexOf("battery") !== -1 || d.indexOf("batteries") !== -1 || d.indexOf("energy storage") !== -1 || d.indexOf("storage system") !== -1) return "Battery";
  return null;
}
const WHY_NOW: Record<string, string> = {
  installers: "Fresh install. The street is warm right now, prime for canvassing the block.",
  cleaning: "First service window. Systems this age are due for a first clean and tune-up.",
  battery: "Storage retrofit window. Past the early years and prime for a battery add-on.",
  takeover: "Service-partner window. Aging system, a candidate to take over from the original installer.",
  loanpayoff: "Loan-payoff window. The upgrade and refinance conversation opens around this age.",
  longterm: "Long-tenure system. A strong full-replacement candidate at this age.",
};
function batteryClause(segment: string, battery: string | null): string {
  if (!battery) return "";
  const name = battery === "Battery" ? "a battery" : "a " + battery;
  if (segment === "battery") return " Note: this one already has " + (battery === "Battery" ? "storage" : battery) + ".";
  if (segment === "installers") return " Already paired with " + name + ".";
  return " It runs " + name + ", so there is more system to service.";
}

type Props = { segment: string; onBack: () => void; onShowList?: () => void };

export default function NearbyMap({ segment, onBack, onShowList }: Props) {
  const [activeSeg, setActiveSeg] = useState<string>(segment || "all");
  const meta = SEG[activeSeg] || SEG.cleaning;
  const [status, setStatus] = useState<"idle" | "locating" | "denied" | "ready">("idle");
  const [features, setFeatures] = useState<any[]>([]);
  const [mode, setMode] = useState<"points" | "clusters">("clusters");
  const [totalInView, setTotalInView] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedLight, setSelectedLight] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [hint, setHint] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [toast, setToast] = useState<string>("");

  const [user, setUser] = useState<any>(null);
  const [unlockedById, setUnlockedById] = useState<Record<string, any>>({});
  const [unlockBusy, setUnlockBusy] = useState<boolean>(false);
  const [unlockMsg, setUnlockMsg] = useState<string>("");
  const [complianceOpen, setComplianceOpen] = useState<boolean>(false);
  const [pendingUnlockId, setPendingUnlockId] = useState<number | null>(null);
  const [focusOpen, setFocusOpen] = useState<boolean>(false);
  const [focusLists, setFocusLists] = useState<any[]>([]);
  const [focusLoading, setFocusLoading] = useState<boolean>(false);
  const [focusBusy, setFocusBusy] = useState<boolean>(false);
  const [newListName, setNewListName] = useState<string>("");

  const mapElRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const LRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const markerByIdRef = useRef<Record<string, any>>({});
  const reqIdRef = useRef<number>(0);
  const debounceRef = useRef<any>(null);
  const pendingRef = useRef<any>(null);
  const detailCacheRef = useRef<Record<string, any>>({});
  const detailReqRef = useRef<number>(0);
  const toastRef = useRef<any>(null);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/me`).then((r) => r.json()).then((j) => { if (j && j.success && j.user) setUser(j.user); }).catch(() => {});
  }, []);

  const flashToast = (msg: string) => {
    setToast(msg);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(""), 2800);
  };

  const fetchViewport = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const z = Math.round(map.getZoom());
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const qs = new URLSearchParams();
    if (activeSeg && activeSeg !== "all") qs.set("segment", activeSeg);
    qs.set("n", String(b.getNorth())); qs.set("s", String(b.getSouth()));
    qs.set("e", String(b.getEast()));  qs.set("w", String(b.getWest()));
    qs.set("z", String(z));
    fetch(`${API_URL}/api/leads/map?${qs.toString()}`)
      .then((r) => r.json())
      .then((j) => {
        if (myReq !== reqIdRef.current) return;
        setLoading(false);
        if (!j || !j.success) return;
        setMode(j.mode === "points" ? "points" : "clusters");
        setTotalInView(Number(j.total) || 0);
        setFeatures(Array.isArray(j.features) ? j.features : []);
      })
      .catch(() => { if (myReq === reqIdRef.current) setLoading(false); });
  };

  // Re-fetch the viewport when the active segment changes (selector chip switch).
  useEffect(() => {
    if (mapRef.current) fetchViewport();
  }, [activeSeg]);

  const scheduleFetch = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchViewport, DEBOUNCE_MS);
  };

  const applyTarget = (t: any) => {
    const map = mapRef.current, L = LRef.current;
    if (!map || !L) { pendingRef.current = t; return; }
    if (t.bbox) map.fitBounds([[t.bbox.s, t.bbox.w], [t.bbox.n, t.bbox.e]], { padding: [24, 24], maxZoom: 16 });
    else map.setView([t.lat, t.lng], t.zoom || 14);
  };

  const teardownMap = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (mapRef.current) { try { mapRef.current.remove(); } catch { /* noop */ } mapRef.current = null; }
    layerRef.current = null; markerByIdRef.current = {}; pendingRef.current = null;
  };

  useEffect(() => {
    if (status !== "ready" || !mapElRef.current || mapRef.current) return;
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !mapElRef.current || mapRef.current) return;
      LRef.current = L;
      const map = L.map(mapElRef.current, { zoomControl: true, preferCanvas: true, worldCopyJump: true, minZoom: 4 });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      map.on("moveend", scheduleFetch);
      mapRef.current = map;
      if (pendingRef.current) { applyTarget(pendingRef.current); pendingRef.current = null; }
      else { map.setView([39.5, -98.35], 4); }
      setTimeout(() => { try { map.invalidateSize(); } catch { /* noop */ } }, 120);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [status]);

  useEffect(() => {
    const map = mapRef.current, L = LRef.current, layer = layerRef.current;
    if (!map || !L || !layer) return;
    layer.clearLayers();
    markerByIdRef.current = {};
    features.forEach((f) => {
      const la = parseFloat(f.lat), ln = parseFloat(f.lng);
      if (!isFinite(la) || !isFinite(ln)) return;
      if (f.t === "c") {
        const n = Number(f.n) || 0;
        const size = n < 10 ? 30 : n < 100 ? 38 : n < 1000 ? 46 : 54;
        const icon = L.divIcon({
          className: "",
          iconSize: [size, size],
          html: '<div style="background:' + meta.color + ';color:#fff;width:' + size + "px;height:" + size +
            'px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:Outfit,sans-serif;' +
            'font-weight:800;font-size:' + (n < 1000 ? 13 : 12) + 'px;border:2px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.35)">' +
            fmtCount(n) + "</div>",
        });
        const m = L.marker([la, ln], { icon });
        m.on("click", () => { map.setView([la, ln], Math.min(Math.round(map.getZoom()) + 3, 19)); });
        layer.addLayer(m);
      } else {
        const cm = L.circleMarker([la, ln], { radius: 7, color: "#FFFFFF", weight: 2, fillColor: meta.color, fillOpacity: 0.92 });
        cm.on("click", () => openCard(f));
        layer.addLayer(cm);
        markerByIdRef.current[String(f.id)] = cm;
      }
    });
  }, [features, meta.color]);

  useEffect(() => {
    const sel = selectedId != null ? String(selectedId) : "";
    Object.keys(markerByIdRef.current).forEach((id) => {
      const m = markerByIdRef.current[id];
      if (!m || !m.setStyle) return;
      if (id === sel) m.setStyle({ radius: 10, color: "#0A0A0A", weight: 3, fillOpacity: 1 });
      else m.setStyle({ radius: 7, color: "#FFFFFF", weight: 2, fillOpacity: 0.92 });
    });
  }, [selectedId, features]);

  useEffect(() => {
    if (selectedId == null) { setDetail(null); setDetailLoading(false); return; }
    const id = String(selectedId);
    if (detailCacheRef.current[id]) { setDetail(detailCacheRef.current[id]); setDetailLoading(false); return; }
    const myReq = ++detailReqRef.current;
    setDetail(null); setDetailLoading(true);
    fetch(`${API_URL}/api/leads/${id}`)
      .then((r) => r.json())
      .then((j) => {
        if (myReq !== detailReqRef.current) return;
        setDetailLoading(false);
        if (j && j.success && j.lead) { detailCacheRef.current[id] = j.lead; setDetail(j.lead); }
      })
      .catch(() => { if (myReq === detailReqRef.current) setDetailLoading(false); });
  }, [selectedId]);

  useEffect(() => {
    return () => {
      if (toastRef.current) clearTimeout(toastRef.current);
      teardownMap();
    };
  }, []);

  const openCard = (f: any) => { setSelectedLight(f); setSelectedId(f.id); setCopied(false); setUnlockMsg(""); };
  const closeCard = () => { setSelectedId(null); setSelectedLight(null); setDetail(null); setUnlockMsg(""); };

  // Back returns to the search screen first, and only exits the feature from there.
  const goBack = () => {
    if (status === "ready") {
      teardownMap();
      setFeatures([]); setMode("clusters"); setTotalInView(0);
      setSelectedId(null); setSelectedLight(null); setDetail(null);
      setStatus("idle");
    } else {
      onBack();
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setStatus("denied"); return; }
    if (status !== "ready") setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setHint(""); if (status !== "ready") setStatus("ready"); applyTarget({ lat: pos.coords.latitude, lng: pos.coords.longitude, zoom: 14 }); },
      (err: any) => { if (status !== "ready") setStatus(err && err.code === 1 ? "denied" : "idle"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const runSearch = () => {
    const q = search.trim();
    if (!q) return;
    setHint("");
    fetch(`${API_URL}/api/geocode?q=${encodeURIComponent(q)}`)
      .then((r) => r.json())
      .then((j) => {
        if (!j || !j.success || !j.found) { setHint((j && j.note) || "Could not find that place. Try a city, zip, or address."); return; }
        if (status !== "ready") setStatus("ready");
        applyTarget({ lat: j.lat, lng: j.lng, bbox: j.bbox, zoom: j.scope === "state" ? 7 : 13 });
      })
      .catch(() => setHint("Could not search right now. Try again."));
  };

  const selectFromList = (f: any) => {
    openCard(f);
    const map = mapRef.current;
    if (map) { const la = parseFloat(f.lat), ln = parseFloat(f.lng); if (isFinite(la) && isFinite(ln)) map.panTo([la, ln], { animate: true }); }
  };

  const openDirections = (f: any) => {
    const q = [f.address, f.city, f.province || f.state].filter(Boolean).join(", ");
    window.open("https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(q), "_blank", "noopener");
  };
  const copyAddress = (f: any) => {
    const q = [f.address, f.city, f.province || f.state].filter(Boolean).join(", ");
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(q).then(() => { setCopied(true); flashToast("Address copied"); }).catch(() => {});
  };

  const openFocusPicker = () => {
    setFocusOpen(true); setNewListName(""); setFocusLoading(true);
    fetch(`${API_URL}/api/focus`).then((r) => r.json()).then((j) => {
      setFocusLoading(false);
      if (j && j.success) setFocusLists(Array.isArray(j.lists) ? j.lists : []);
    }).catch(() => setFocusLoading(false));
  };
  const saveToList = (listId: any, listTitle: string) => {
    if (!selectedId || focusBusy) return;
    setFocusBusy(true);
    fetch(`${API_URL}/api/focus/${listId}/leads`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ leadIds: [selectedId] }),
    }).then((r) => r.json()).then((j) => {
      setFocusBusy(false);
      if (j && j.success) { setFocusOpen(false); flashToast("Saved to " + listTitle); }
      else flashToast("Could not save. Try again.");
    }).catch(() => { setFocusBusy(false); flashToast("Could not save. Try again."); });
  };
  const createListAndSave = () => {
    const title = newListName.trim();
    if (!title || !selectedId || focusBusy) return;
    setFocusBusy(true);
    fetch(`${API_URL}/api/focus`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title, leadIds: [selectedId] }),
    }).then((r) => r.json()).then((j) => {
      setFocusBusy(false);
      if (j && j.success) { setFocusOpen(false); flashToast("Saved to " + title); }
      else flashToast("Could not create list. Try again.");
    }).catch(() => { setFocusBusy(false); flashToast("Could not create list. Try again."); });
  };

  const requestUnlock = () => {
    if (!selectedId || unlockBusy) return;
    if (unlockedById[String(selectedId)]) return;
    if (!user || !user.skiptraceAckAt) { setPendingUnlockId(selectedId); setComplianceOpen(true); return; }
    doUnlock(selectedId);
  };
  const acceptCompliance = () => {
    fetch(`${API_URL}/api/user/acknowledge-skiptrace`, { method: "POST" })
      .then((r) => r.json())
      .then((j) => {
        setComplianceOpen(false);
        if (j && j.success) {
          setUser((u: any) => ({ ...(u || {}), skiptraceAckAt: j.skiptraceAckAt || new Date().toISOString() }));
          const id = pendingUnlockId; setPendingUnlockId(null);
          if (id) doUnlock(id);
        }
      })
      .catch(() => setComplianceOpen(false));
  };
  const doUnlock = (id: number) => {
    setUnlockBusy(true); setUnlockMsg("");
    fetch(`${API_URL}/api/leads/${id}/unlock`, { method: "POST" })
      .then((r) => r.json())
      .then((j) => {
        setUnlockBusy(false);
        if (j && j.success && j.lead) {
          setUnlockedById((m) => ({ ...m, [String(id)]: j.lead }));
          flashToast("Contact unlocked \u00b7 saved to Verified");
          if (Number(j.cost) === 1 && user && typeof user.creditsRemaining === "number") {
            setUser((u: any) => ({ ...(u || {}), creditsRemaining: Math.max(0, (u.creditsRemaining || 0) - 1) }));
          }
        } else {
          setUnlockMsg((j && j.error) || "No contact found.");
        }
      })
      .catch(() => { setUnlockBusy(false); setUnlockMsg("Lookup failed. Try again."); });
  };

  const points = features.filter((f) => f.t === "p");
  points.sort((a: any, b: any) => {
    const ta = Date.parse(String(a.permit_date || "")) || 0;
    const tb = Date.parse(String(b.permit_date || "")) || 0;
    return (activeSeg === "installers" || activeSeg === "all") ? tb - ta : ta - tb;
  });
  const listLabel = activeSeg === "all" ? "Most recent" : (activeSeg === "installers" ? "Freshest installs" : "Most due (oldest first)");
  const showHero = status === "idle" || status === "denied";

  const card = detail || selectedLight;
  const cardProv = card ? (card.province || card.state || "") : "";
  const cardAge = card ? ageParts(card.permit_date) : { short: "", long: "" };
  const intel = detail && detail.intel ? detail.intel : null;
  const contractor = detail ? detail.contractor_name : "";
  const permitType = detail ? cleanPermitType(detail.permit_type) : "";
  const descr = detail && detail.description ? String(detail.description).trim() : "";
  const sigScore = detail && detail.signal_score != null ? Number(detail.signal_score) : null;
  const ctrIntel = intel && intel.contractor ? intel.contractor : null;
  const mom = intel && intel.momentum ? intel.momentum : null;
  const dens = intel && intel.density ? intel.density : null;
  const monthly: number[] = mom && Array.isArray(mom.monthly) ? mom.monthly.map((x: any) => Number(x) || 0) : [];
  const momMax = monthly.length ? Math.max.apply(null, monthly.concat([1])) : 1;
  const trendLabel = mom ? (mom.trend === "accelerating" ? "Accelerating" : mom.trend === "cooling" ? "Cooling" : "Steady") : "";
  const unlocked = card && selectedId != null ? unlockedById[String(selectedId)] : null;
  const isAdmin = !!(user && user.isAdmin);
  const credits = user && typeof user.creditsRemaining === "number" ? user.creditsRemaining : null;
  const sysKw = parseKw(descr);
  const batteryLabel = parseBattery(descr);
  const whyNow = (WHY_NOW[activeSeg] || WHY_NOW.cleaning) + batteryClause(activeSeg, batteryLabel);

  return (
    <div className="nm">
      <style>{`
        .nm{font-family:"Outfit",sans-serif;color:#0A0A0A;background:#F7F8F5;min-height:100%;}
        .nm-back{display:flex;align-items:center;gap:6px;color:#1A1A1A;font-weight:700;font-size:15px;background:none;border:0;cursor:pointer;padding:0;margin:14px 0 0 18px;}
        .nm-back svg{width:16px;height:16px;}
        .nm-topnav{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 18px 0;}
        .nm-topnav .nm-back{margin:0;}
        .nm-viewtoggle{display:inline-flex;background:#ECEAE1;border:1px solid rgba(10,10,10,.10);border-radius:12px;padding:4px;gap:4px;}
        .nm-viewtoggle button{display:inline-flex;align-items:center;gap:7px;font-family:"JetBrains Mono",monospace;font-weight:800;font-size:12px;letter-spacing:.08em;text-transform:uppercase;border:0;border-radius:9px;padding:9px 16px;cursor:pointer;background:transparent;color:#0A0A0A;}
        .nm-viewtoggle button.on{background:#0A0A0A;color:#fff;}
        .nm-viewtoggle svg{width:15px;height:15px;}
        .nm-head{display:flex;align-items:center;gap:13px;padding:10px 18px 14px;}
        .nm-bigdot{width:15px;height:15px;border-radius:50%;flex:none;box-shadow:0 0 0 4px ${meta.color}22;}
        .nm-h1{font-size:31px;font-weight:900;letter-spacing:-.035em;line-height:1;}
        .nm-tag{font-size:14px;font-weight:600;color:#2A2A33;margin-top:6px;letter-spacing:-.01em;}
        .nm-count{font-family:"JetBrains Mono",monospace;font-size:12.5px;font-weight:600;color:${meta.color};margin-top:6px;}

        .nm-bar{display:flex;gap:8px;align-items:center;padding:2px 18px 10px;}
        .nm-bar input{flex:1;border:1.5px solid #E3E3E3;border-radius:11px;padding:12px 14px;font-family:"Outfit";font-size:15px;outline:none;background:#fff;}
        .nm-bar input:focus{border-color:#0A0A0A;}
        .nm-bar .go{border:0;background:#0A0A0A;color:#fff;border-radius:11px;padding:0 18px;height:44px;font-weight:800;font-size:14px;cursor:pointer;}
        .nm-bar .loc{display:inline-flex;align-items:center;justify-content:center;border:1.5px solid #E3E3E3;background:#fff;border-radius:11px;width:44px;height:44px;cursor:pointer;flex:none;}
        .nm-bar .loc svg{width:18px;height:18px;}

        /* ── Hero ── */
        .nm-hero{position:relative;margin:4px 18px 0;background:#0A0A0A;color:#fff;border-radius:22px;padding:44px 36px 30px;overflow:hidden;}
        .nm-hero::after{content:"";position:absolute;right:-30px;top:-60px;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle, ${meta.color}55, transparent 66%);}
        .nm-eyebrow{position:relative;display:inline-flex;align-items:center;gap:9px;font-family:"JetBrains Mono",monospace;font-size:11px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:#fff;margin-bottom:18px;}
        .nm-eyebrow .d{width:9px;height:9px;border-radius:50%;background:${meta.color};box-shadow:0 0 12px ${meta.color};}
        .nm-hero h2{position:relative;margin:0 0 13px;font-size:35px;line-height:1.02;font-weight:900;letter-spacing:-.04em;max-width:560px;color:#fff;}
        .nm-hero p{position:relative;margin:0 0 26px;font-size:16px;line-height:1.5;color:#fff;opacity:.92;max-width:540px;}
        .nm-herosrch{position:relative;display:flex;gap:10px;max-width:560px;}
        .nm-herosrch input{flex:1;border:0;border-radius:13px;padding:16px 17px;font-family:"Outfit";font-size:16px;outline:none;background:#fff;color:#0A0A0A;font-weight:500;}
        .nm-herosrch .go{border:0;background:${meta.color};color:#fff;border-radius:13px;padding:0 28px;font-family:"Outfit";font-weight:900;font-size:15px;cursor:pointer;}
        .nm-loclink{position:relative;display:inline-flex;align-items:center;gap:8px;margin-top:18px;background:none;border:0;color:#fff;font-family:"Outfit";font-weight:700;font-size:14px;cursor:pointer;padding:0;}
        .nm-loclink svg{width:16px;height:16px;color:${meta.color};}
        .nm-stats{position:relative;margin-top:28px;padding-top:20px;border-top:1px solid rgba(255,255,255,.18);font-family:"JetBrains Mono",monospace;font-size:12px;letter-spacing:.03em;color:#fff;opacity:1;}
        .nm-stats b{color:#fff;font-weight:700;opacity:1;}
        .nm-msg{position:relative;margin-top:16px;font-family:"JetBrains Mono",monospace;font-size:12px;color:#FF9B9B;line-height:1.5;}
        .nm-busy{margin:40px 18px;text-align:center;font-family:"JetBrains Mono",monospace;font-size:13px;color:#262B37;}

        .nm-mapwrap{position:relative;margin:0 18px;}
        .nm-map{height:58vh;min-height:340px;border-radius:16px;overflow:hidden;border:1px solid #ECECEC;background:#eef0ea;z-index:0;}
        .nm-pill{position:absolute;top:12px;left:50%;transform:translateX(-50%);z-index:5;background:#0A0A0A;color:#fff;font-family:"JetBrains Mono",monospace;font-size:11px;font-weight:700;padding:6px 12px;border-radius:999px;box-shadow:0 2px 10px rgba(0,0,0,.2);}
        .nm-hintbar{padding:10px 18px 0;font-family:"JetBrains Mono",monospace;font-size:11.5px;color:#262B37;text-align:center;}

        .nm-listhead{display:flex;justify-content:space-between;align-items:baseline;padding:16px 18px 8px;}
        .nm-listhead .h{font-size:17px;font-weight:900;letter-spacing:-.01em;}
        .nm-listhead .t{font-family:"JetBrains Mono",monospace;font-size:11px;color:#262B37;}
        .nm-row{display:flex;align-items:center;gap:12px;margin:0 18px 8px;background:#fff;border:1px solid #ECECEC;border-radius:13px;padding:11px 14px;cursor:pointer;}
        .nm-row.sel{border-color:${meta.color};box-shadow:0 0 0 1px ${meta.color};}
        .nm-rk{font-family:"JetBrains Mono",monospace;font-weight:700;font-size:12px;color:#262B37;width:24px;flex:none;}
        .nm-bodyc{flex:1;min-width:0;}
        .nm-addr{font-weight:800;font-size:14.5px;letter-spacing:-.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .nm-rsub{font-family:"JetBrains Mono",monospace;font-size:11px;color:#262B37;margin-top:3px;}
        .nm-metric{font-family:"JetBrains Mono",monospace;font-weight:700;font-size:13px;flex:none;color:${meta.color};text-align:right;}
        .nm-metric small{display:block;font-weight:400;font-size:10px;color:#262B37;}
        .nm-empty{margin:22px 18px;text-align:center;font-family:"JetBrains Mono",monospace;font-size:12.5px;color:#262B37;line-height:1.6;}
        .nm-pad{height:140px;}

        /* ── Rich detail sheet ── */
        .nm-sheet{position:fixed;left:50%;transform:translateX(-50%) translateY(140%);bottom:18px;width:min(480px,calc(100% - 28px));max-height:85vh;display:flex;flex-direction:column;background:#fff;border-radius:22px;box-shadow:0 18px 54px rgba(10,10,10,.30);z-index:60;transition:transform .26s cubic-bezier(.2,.8,.2,1);overflow:hidden;}
        .nm-sheet.up{transform:translateX(-50%) translateY(0);}
        .nm-accent{height:5px;background:${meta.color};flex:none;}
        .nm-x{position:absolute;top:15px;right:15px;background:#F1F1EF;border:0;cursor:pointer;color:#262B37;font-size:17px;line-height:1;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;z-index:2;}
        .nm-scroll{overflow-y:auto;padding:18px 20px 6px;}
        .nm-seg{display:inline-flex;align-items:center;gap:7px;font-family:"JetBrains Mono",monospace;font-size:11px;font-weight:700;color:${meta.color};letter-spacing:.09em;text-transform:uppercase;margin-bottom:10px;}
        .nm-seg .d{width:9px;height:9px;border-radius:50%;background:${meta.color};}
        .nm-cardtop{display:flex;align-items:flex-start;gap:8px;}
        .nm-cardaddr{flex:1;font-size:23px;font-weight:900;letter-spacing:-.03em;line-height:1.05;}
        .nm-copy{flex:none;background:#F1F1EF;border:0;border-radius:9px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#262B37;}
        .nm-copy svg{width:15px;height:15px;}
        .nm-cardcity{font-family:"JetBrains Mono",monospace;font-size:12.5px;color:#1A1A1A;margin-top:6px;}

        .nm-why{margin-top:16px;background:${meta.color}14;border-radius:14px;padding:13px 15px;}
        .nm-why .lab{display:block;font-family:"JetBrains Mono",monospace;font-size:10px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:${meta.color};margin-bottom:5px;}
        .nm-why .txt{font-family:"Outfit";font-weight:700;font-size:15px;line-height:1.4;color:#0A0A0A;}

        .nm-specs{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:14px;}
        .nm-tile{border-radius:13px;background:#F1F1EF;padding:13px 14px;}
        .nm-tile.hot{background:${meta.color}16;}
        .nm-tile b{display:block;font-family:"Outfit";font-weight:900;font-size:22px;letter-spacing:-.03em;color:#0A0A0A;line-height:1;}
        .nm-tile.hot b{color:${meta.color};}
        .nm-tile span{display:block;font-family:"JetBrains Mono",monospace;font-size:9.5px;color:#0A0A0A;letter-spacing:.08em;text-transform:uppercase;margin-top:8px;font-weight:700;}
        .nm-tile.batt{background:${meta.color};display:flex;flex-direction:column;justify-content:center;}
        .nm-tile.batt b{color:#fff;font-size:16px;display:flex;align-items:center;gap:7px;}
        .nm-tile.batt b svg{width:17px;height:17px;}
        .nm-tile.batt span{color:#fff;}

        .nm-blk{margin-top:20px;}
        .nm-blk h4{margin:0 0 9px;font-family:"JetBrains Mono",monospace;font-size:10.5px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:#1A1A1A;}
        .nm-ctr{font-size:16px;font-weight:800;letter-spacing:-.015em;line-height:1.2;}
        .nm-ctrstat{font-family:"JetBrains Mono",monospace;font-size:12px;color:#1A1A1A;margin-top:6px;line-height:1.5;}
        .nm-ctrstat b{color:${meta.color};font-weight:700;}

        .nm-area-head{font-family:"Outfit";font-weight:800;font-size:17px;letter-spacing:-.01em;color:#0A0A0A;line-height:1.3;}
        .nm-area-head b{color:${meta.color};}
        .nm-spark{display:flex;align-items:flex-end;gap:3px;height:42px;margin-top:11px;}
        .nm-spark i{flex:1;border-radius:2px 2px 0 0;min-height:3px;display:block;background:${meta.color};}
        .nm-sparkfoot{display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-family:"JetBrains Mono",monospace;font-size:10px;color:#1A1A1A;}
        .nm-chip{font-family:"JetBrains Mono",monospace;font-size:10px;font-weight:800;letter-spacing:.06em;padding:3px 9px;border-radius:999px;text-transform:uppercase;}
        .nm-dens{font-family:"JetBrains Mono",monospace;font-size:12px;color:#262B37;margin-top:11px;line-height:1.5;}
        .nm-dens b{color:#0A0A0A;}
        .nm-desc{margin-top:8px;font-size:13px;line-height:1.5;color:#1A1A1A;background:#F4F4F2;border-radius:11px;padding:11px 13px;}

        /* owner contact / unlock - the money block, kept high */
        .nm-own{margin-top:20px;background:${meta.color}10;border:1.5px solid ${meta.color}40;border-radius:16px;padding:16px;}
        .nm-own h4{margin:0 0 11px;color:#0A0A0A;letter-spacing:.13em;}
        .nm-unlockbtn{width:100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;background:${meta.color};color:#fff;border:0;border-radius:12px;padding:15px;font-family:"Outfit";font-weight:900;font-size:15px;cursor:pointer;}
        .nm-unlockbtn:disabled{opacity:.6;cursor:default;}
        .nm-unlockbtn svg{width:17px;height:17px;}
        .nm-unlocknote{font-family:"JetBrains Mono",monospace;font-size:10.5px;color:#1A1A1A;text-align:center;margin-top:10px;letter-spacing:.02em;line-height:1.5;}
        .nm-unlockmsg{font-family:"JetBrains Mono",monospace;font-size:11.5px;color:#C0392B;text-align:center;margin-top:10px;line-height:1.5;}
        .nm-contact{display:flex;flex-direction:column;gap:10px;}
        .nm-crow{display:flex;align-items:center;gap:10px;}
        .nm-crow .lab{font-family:"JetBrains Mono",monospace;font-size:9.5px;color:#262B37;text-transform:uppercase;letter-spacing:.07em;width:46px;flex:none;font-weight:700;}
        .nm-crow .val{font-size:15px;font-weight:800;letter-spacing:-.01em;}
        .nm-crow a{color:#0A0A0A;text-decoration:none;}
        .nm-dnc{font-family:"JetBrains Mono",monospace;font-size:9px;font-weight:800;color:#fff;background:#B91C1C;border-radius:5px;padding:2px 6px;letter-spacing:.05em;}
        .nm-verified{display:inline-flex;align-items:center;gap:6px;margin-top:12px;font-family:"JetBrains Mono",monospace;font-size:10.5px;font-weight:700;color:${meta.color};letter-spacing:.03em;}
        .nm-verified svg{width:13px;height:13px;}

        .nm-src{margin-top:18px;font-family:"JetBrains Mono",monospace;font-size:10px;color:#1A1A1A;letter-spacing:.03em;}
        .nm-skel{height:13px;border-radius:6px;background:linear-gradient(90deg,#F0F0EE,#E7E7E4,#F0F0EE);background-size:200% 100%;animation:nmsh 1.2s infinite;margin-top:12px;}
        @keyframes nmsh{0%{background-position:200% 0}100%{background-position:-200% 0}}

        .nm-acts{flex:none;display:flex;gap:10px;padding:14px 20px 18px;border-top:1px solid #F0F0EE;}
        .nm-acts button{flex:1;display:inline-flex;align-items:center;justify-content:center;gap:7px;border-radius:12px;padding:14px;font-family:"Outfit";font-weight:800;font-size:14.5px;cursor:pointer;border:0;}
        .nm-acts .primary{background:#0A0A0A;color:#fff;}
        .nm-acts .focus{background:${meta.color};color:#fff;font-weight:900;}
        .nm-acts svg{width:16px;height:16px;}

        .nm-back-drop{position:fixed;inset:0;background:rgba(10,10,10,.5);z-index:80;display:flex;align-items:center;justify-content:center;padding:18px;}
        .nm-modal{width:min(440px,100%);max-height:86vh;overflow-y:auto;background:#fff;border-radius:20px;padding:24px;box-shadow:0 24px 60px rgba(0,0,0,.4);}
        .nm-modal h3{margin:0 0 6px;font-size:21px;font-weight:900;letter-spacing:-.025em;}
        .nm-modal .msub{font-family:"JetBrains Mono",monospace;font-size:11.5px;color:#262B37;margin-bottom:16px;}
        .nm-fl{display:flex;align-items:center;justify-content:space-between;gap:10px;width:100%;text-align:left;background:#F4F4F2;border:1px solid #E8E8E4;border-radius:12px;padding:13px 15px;cursor:pointer;margin-bottom:8px;}
        .nm-fl:hover{border-color:${meta.color};}
        .nm-fl .t{font-weight:800;font-size:15px;letter-spacing:-.01em;}
        .nm-fl .c{font-family:"JetBrains Mono",monospace;font-size:11px;color:#262B37;flex:none;}
        .nm-or{font-family:"JetBrains Mono",monospace;font-size:10px;color:#262B37;letter-spacing:.1em;text-align:center;margin:14px 0;}
        .nm-new{display:flex;gap:8px;}
        .nm-new input{flex:1;border:1.5px solid #E3E3E3;border-radius:11px;padding:12px 13px;font-family:"Outfit";font-size:14px;outline:none;}
        .nm-new input:focus{border-color:#0A0A0A;}
        .nm-new button{border:0;background:#0A0A0A;color:#fff;border-radius:11px;padding:0 16px;font-weight:800;font-size:13.5px;cursor:pointer;}
        .nm-new button:disabled{opacity:.5;cursor:default;}
        .nm-comply p{font-size:13.5px;line-height:1.6;color:#262B37;margin:0 0 12px;}
        .nm-comply .fine{font-family:"JetBrains Mono",monospace;font-size:10.5px;color:#262B37;margin-bottom:18px;}
        .nm-mbtns{display:flex;gap:10px;}
        .nm-mbtns button{flex:1;border-radius:12px;padding:14px;font-family:"Outfit";font-weight:800;font-size:14.5px;cursor:pointer;}
        .nm-mbtns .agree{border:0;background:#0A0A0A;color:#fff;}
        .nm-mbtns .cancel{background:#fff;color:#262B37;border:1.5px solid #E3E3E3;}

        .nm-toast{position:fixed;bottom:26px;left:50%;transform:translateX(-50%);z-index:95;background:#0A0A0A;color:#fff;font-family:"Outfit";font-weight:700;font-size:14px;padding:13px 22px;border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.3);display:flex;align-items:center;gap:9px;}
        .nm-toast .d{width:9px;height:9px;border-radius:50%;background:${meta.color};}

        /* Mobile: the bottom nav is 64px tall, so lift the sheet above it and pad for the home indicator. */
        @media (max-width: 768px){
          .nm-sheet{bottom:calc(76px + env(safe-area-inset-bottom, 0px));max-height:78vh;}
          .nm-acts{padding-bottom:16px;}
        }
      `}</style>

      <div className="nm-topnav">
        <button className="nm-back" onClick={goBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6" /></svg>
          Back
        </button>
        {onShowList && (
          <div className="nm-viewtoggle">
            <button onClick={onShowList}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
              List
            </button>
            <button className="on">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>
              Map
            </button>
          </div>
        )}
      </div>
      <div className="nm-head">
        <span className="nm-bigdot" style={{ background: meta.color }} />
        <div>
          <div className="nm-h1">{meta.label}</div>
          {status === "ready"
            ? <div className="nm-count">{totalInView.toLocaleString()} homes in view{mode === "clusters" ? " \u00b7 zoom in for doors" : ""}</div>
            : <div className="nm-tag">The entire market, mapped to the door.</div>}
        </div>
      </div>

      <div className="nm-segsel" style={{ display: "flex", gap: 8, overflowX: "auto", padding: "2px 18px 8px" }}>
        {(["all", "installers", "cleaning", "battery", "takeover", "loanpayoff", "longterm"]).map((k) => {
          const on = activeSeg === k;
          const col = k === "all" ? "#0A0A0A" : ((SEG[k] && SEG[k].color) || "#0A0A0A");
          const labels: Record<string, string> = { all: "All", installers: "New", cleaning: "Maintenance", battery: "Battery", takeover: "Takeover", loanpayoff: "Loan payoff", longterm: "Replacement" };
          return (
            <button key={k} onClick={() => setActiveSeg(k)} style={{ flex: "0 0 auto", cursor: "pointer", fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", padding: "8px 13px", borderRadius: 9, border: on ? ("1.5px solid " + col) : "1.5px solid #E2E2DA", background: on ? col : "#FFFFFF", color: on ? "#FFFFFF" : "#0A0A0A", whiteSpace: "nowrap" }}>
              {labels[k]}
            </button>
          );
        })}
      </div>

      {showHero && (
        <div className="nm-hero">
          <div className="nm-eyebrow"><span className="d" />{meta.label}</div>
          <h2>{meta.heroTitle}</h2>
          <p>{meta.heroSub}</p>
          <div className="nm-herosrch">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }} placeholder="City, state, zip, or address" />
            <button className="go" onClick={runSearch}>Search</button>
          </div>
          <button className="nm-loclink" onClick={useMyLocation}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3" /><path d="M12 21s-7-6.3-7-11a7 7 0 1114 0c0 4.7-7 11-7 11z" /></svg>
            or use my current location
          </button>
          <div className="nm-stats"><b>950,000+ verified permits</b> {"\u00b7"} refreshed daily {"\u00b7"} U.S. and Canada</div>
          {status === "denied" && <div className="nm-msg">Location is off or blocked for this site. Turn it on in settings, or just search an area above.</div>}
          {hint && <div className="nm-msg">{hint}</div>}
        </div>
      )}

      {status === "locating" && <div className="nm-busy">Finding your location...</div>}

      {status === "ready" && (
        <>
          <div className="nm-bar">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }} placeholder="Jump to a city, state, zip, or address" />
            <button className="go" onClick={runSearch}>Go</button>
            <button className="loc" onClick={useMyLocation} aria-label="Use my location">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3" /><path d="M12 21s-7-6.3-7-11a7 7 0 1114 0c0 4.7-7 11-7 11z" /></svg>
            </button>
          </div>
          {hint && <div className="nm-hintbar" style={{ color: "#B91C1C" }}>{hint}</div>}

          <div className="nm-mapwrap">
            <div className="nm-map" ref={mapElRef} />
            {loading && <div className="nm-pill">Updating...</div>}
          </div>

          <div className="nm-hintbar">Pan and zoom to load more. Clusters split into individual doors as you zoom in.</div>

          <div className="nm-listhead">
            <span className="h">{mode === "points" ? listLabel : "In this view"}</span>
            <span className="t">{mode === "points" ? (points.length > LIST_MAX ? `top ${LIST_MAX} of ${points.length}` : `${points.length} homes`) : "zoom in to list"}</span>
          </div>

          {mode === "clusters" ? (
            <div className="nm-empty">{totalInView.toLocaleString()} homes in view. Zoom in to load individual doors and work them.</div>
          ) : points.length === 0 ? (
            <div className="nm-empty">No {meta.label.toLowerCase()} homes in this view. Pan or zoom out to find more.</div>
          ) : (
            points.slice(0, LIST_MAX).map((f, i) => {
              const a = ageParts(f.permit_date);
              return (
                <div key={f.id} className={"nm-row" + (selectedId === f.id ? " sel" : "")} onClick={() => selectFromList(f)}>
                  <span className="nm-rk">{i + 1}</span>
                  <div className="nm-bodyc">
                    <div className="nm-addr">{f.address}</div>
                    <div className="nm-rsub">{f.city}{f.province ? ", " + f.province : ""}</div>
                  </div>
                  <span className="nm-metric">{a.short || "n/a"}<small>since install</small></span>
                </div>
              );
            })
          )}
          <div className="nm-pad" />
        </>
      )}

      <div className={"nm-sheet" + (card ? " up" : "")}>
        {card && (
          <>
            <div className="nm-accent" />
            <button className="nm-x" onClick={closeCard} aria-label="Close">×</button>
            <div className="nm-scroll">
              <div className="nm-seg"><span className="d" />{meta.label}</div>
              <div className="nm-cardtop">
                <div className="nm-cardaddr">{card.address}</div>
                <button className="nm-copy" onClick={() => copyAddress(card)} aria-label="Copy address" title={copied ? "Copied" : "Copy address"}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg>
                </button>
              </div>
              <div className="nm-cardcity">{card.city}{cardProv ? ", " + cardProv : ""}</div>

              <div className="nm-why">
                <span className="lab">Why now</span>
                <span className="txt">{whyNow}</span>
              </div>

              <div className="nm-specs">
                <div className="nm-tile hot"><b>{cardAge.short || "n/a"}</b><span>System age</span></div>
                {sysKw && <div className="nm-tile"><b>{sysKw}</b><span>System size</span></div>}
                {batteryLabel && (
                  <div className="nm-tile batt">
                    <b><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="8" width="14" height="9" rx="2.5" /><path d="M21 11v3" strokeLinecap="round" /></svg>{batteryLabel}</b>
                    <span>Battery on site</span>
                  </div>
                )}
                <div className="nm-tile"><b>{cardAge.long || "n/a"}</b><span>Installed</span></div>
                {segment === "installers" && sigScore != null && (
                  <div className="nm-tile"><b>{sigScore.toFixed(1)}</b><span>Signal / 10</span></div>
                )}
              </div>

              <div className="nm-own">
                <h4>Owner contact</h4>
                {unlocked ? (
                  <div className="nm-contact">
                    {unlocked.owner_name && <div className="nm-crow"><span className="lab">Name</span><span className="val">{unlocked.owner_name}</span></div>}
                    {unlocked.owner_phone && (
                      <div className="nm-crow"><span className="lab">Phone</span><span className="val"><a href={"tel:" + unlocked.owner_phone}>{unlocked.owner_phone}</a></span>{unlocked.dnc && <span className="nm-dnc">DNC</span>}</div>
                    )}
                    {unlocked.owner_email && <div className="nm-crow"><span className="lab">Email</span><span className="val" style={{ fontSize: 13, wordBreak: "break-all" }}><a href={"mailto:" + unlocked.owner_email}>{unlocked.owner_email}</a></span></div>}
                    <div className="nm-verified">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                      Saved to your Verified list
                    </div>
                  </div>
                ) : (
                  <>
                    <button className="nm-unlockbtn" onClick={requestUnlock} disabled={unlockBusy}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V7a4 4 0 018 0" /></svg>
                      {unlockBusy ? "Looking up..." : "Unlock owner contact"}
                    </button>
                    <div className="nm-unlocknote">{isAdmin ? "Admin \u00b7 no credit charged \u00b7 files to Verified" : `Uses 1 credit${credits != null ? " \u00b7 " + credits.toLocaleString() + " left" : ""}. Nothing charged if no contact is found.`}</div>
                    {unlockMsg && <div className="nm-unlockmsg">{unlockMsg}</div>}
                  </>
                )}
              </div>

              {detailLoading && !detail && (
                <>
                  <div className="nm-skel" style={{ width: "40%" }} />
                  <div className="nm-skel" style={{ width: "85%" }} />
                  <div className="nm-skel" style={{ width: "70%" }} />
                </>
              )}

              {contractor && (
                <div className="nm-blk">
                  <h4>Installed by</h4>
                  <div className="nm-ctr">{contractor}</div>
                  {ctrIntel && Number(ctrIntel.cityPermitsAllTime) > 0 && (
                    <div className="nm-ctrstat">
                      <b>{Number(ctrIntel.cityPermitsAllTime).toLocaleString()}</b> permits in {ctrIntel.city}
                      {Number(ctrIntel.cityPermits90d) > 0 ? <>, <b>{ctrIntel.cityPermits90d}</b> in the last 90 days</> : null}
                    </div>
                  )}
                </div>
              )}

              {(dens || (mom && monthly.length > 0)) && (
                <div className="nm-blk">
                  <h4>Neighborhood</h4>
                  {dens && Number(dens.count90d) > 0 ? (
                    <div className="nm-area-head"><b>{Number(dens.count90d).toLocaleString()}</b> nearby solar {Number(dens.count90d) === 1 ? "permit" : "permits"} in {dens.label}, last 90 days</div>
                  ) : (
                    <div className="nm-area-head">Local solar activity</div>
                  )}
                  {mom && monthly.length > 0 && (
                    <>
                      <div className="nm-spark">
                        {monthly.map((v, i) => (
                          <i key={i} style={{ height: Math.max(3, Math.round((v / momMax) * 42)), opacity: i >= monthly.length - 3 ? 1 : 0.34 }} />
                        ))}
                      </div>
                      <div className="nm-sparkfoot"><span>{mom.startLabel}</span><span>{mom.endLabel}</span></div>
                    </>
                  )}
                </div>
              )}

              {descr && (<div className="nm-blk"><h4>Scope</h4><div className="nm-desc">{descr}</div></div>)}

              {detail && detail.data_source && <div className="nm-src">Source: {detail.data_source}</div>}
            </div>

            <div className="nm-acts">
              <button className="primary" onClick={() => openDirections(card)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 11l19-9-9 19-2-8-8-2z" /></svg>
                Directions
              </button>
              <button className="focus" onClick={openFocusPicker}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 3v18l7-5 7 5V3z" /></svg>
                Save to Focus
              </button>
            </div>
          </>
        )}
      </div>

      {focusOpen && (
        <div className="nm-back-drop" onClick={() => setFocusOpen(false)}>
          <div className="nm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Save to Focus</h3>
            <div className="msub">{card ? card.address : ""}</div>
            {focusLoading ? (
              <div className="nm-or">Loading your lists...</div>
            ) : (
              <>
                {focusLists.map((l) => (
                  <button key={l.focus_list_id} className="nm-fl" disabled={focusBusy} onClick={() => saveToList(l.focus_list_id, l.title)}>
                    <span className="t">{l.title}</span>
                    <span className="c">{Number(l.total || 0).toLocaleString()} saved</span>
                  </button>
                ))}
                <div className="nm-or">{focusLists.length ? "OR CREATE A NEW LIST" : "CREATE YOUR FIRST LIST"}</div>
                <div className="nm-new">
                  <input value={newListName} onChange={(e) => setNewListName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createListAndSave(); }} placeholder="New list name" />
                  <button disabled={focusBusy || !newListName.trim()} onClick={createListAndSave}>Create</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {complianceOpen && (
        <div className="nm-back-drop">
          <div className="nm-modal nm-comply">
            <h3>Before you unlock contact data</h3>
            <div className="msub">One-time acknowledgment</div>
            {COMPLIANCE.map((p, i) => <p key={i}>{p}</p>)}
            <div className="fine">SolarLead Inc. provides data for lawful business use. This is not legal advice.</div>
            <div className="nm-mbtns">
              <button className="cancel" onClick={() => { setComplianceOpen(false); setPendingUnlockId(null); }}>Cancel</button>
              <button className="agree" onClick={acceptCompliance}>I understand and agree</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="nm-toast"><span className="d" />{toast}</div>}
    </div>
  );
}
